import React from 'react';
import { t, translationExists } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Select from '@material-ui/core/Select';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import { grey } from '@material-ui/core/colors';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useDispatch } from 'react-redux';
import { get } from 'lodash';
import clsx from 'clsx';
import { setSeatPositionsData } from '../../Reducers/Configurator/Actions';
import InfoTooltip from '../InfoTooltip/InfoTooltip';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';

const getFeatureErrorText = (feature) => {
  switch (feature.error) {
    case 'required': {
      return t('errors.required');
    }
    default: {
      throw new Error('Unhandled error type: ' + feature.error);
    }
  }
};

const useStyles = makeStyles((theme) => ({
  root: {
    paddingLeft: 4,
    [theme.breakpoints.down('xs')]: {
      minHeight: 100,
    },
    '&.isLoadInprogress': {
      opacity: 0.7,
      pointerEvents: 'none',
    },
  },
  loadingContainer: {
    position: 'absolute',
    left: '25%',
    top: '25%',
    [theme.breakpoints.down('xs')]: {
      top: '15%',
      left: '40%',
    },
  },
  question: {
    display: 'flex',
    margin: '10px 0px',
    alignItems: 'flex-start',
    [theme.breakpoints.down('xs')]: {
      maxWidth: '100%',
    },
  },
  colorSwitchBase: {
    color: grey[100],
    '&$colorChecked': {
      color: grey[900],
      '& + $colorBar': {
        backgroundColor: grey[500],
      },
    },
  },
  colorBar: {},
  colorChecked: {},
  radio: {
    '&$checked': {
      color: grey[900],
    },
  },
  featureOptions: {
    padding: '0px 30px 8px 16px',
    borderBottom: '1px solid ' + grey[500],
    marginBottom: 12,
  },
  lastFeatureOption: {
    borderBottom: 0,
    marginBottom: 20,
  },
  checked: {},
  optionsGroup: {
    display: 'block',
    marginLeft: 8,
  },
  questionTitle: {
    width: '50%',
    textAlign: 'right',
    paddingRight: 12,
    display: 'flex',
    flex: 1,
    minHeight: 50,
    alignItems: 'center',
  },
  questionTitleInner: {
    display: 'flex',
    flex: 1,
    '& > span': {
      flex: 1,
    },
  },
  tooltip: {
    marginLeft: theme.spacing(1),
  },
  answerSelector: {
    padding: '15px 32px 15px 10px',
    '&:focus': {
      backgroundColor: '#ffffff',
    },
  },
  selectorOutline: {
    width: '50%',
  },
  additionalSectionTitle: {
    padding: '16px 0px',
    fontSize: '1rem !important',
  },
  selectSection: {
    margin: '0px 16px',
  },
  posQuestion: {
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  posSelectorOutline: {
    width: 60,
    marginRight: 8,
    maxHeight: 45,
  },
  posHint: {
    marginBottom: theme.spacing(2),
  },
}));

const ProductVariant = ({ seatPositionsData, additionalPositionsLoaded }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { seatPositions, additionalPositions } = seatPositionsData;
  const onToggle = (seatPosition, value) => {
    const seatPositions = seatPositionsData.seatPositions.map((seatPos) => {
      if (seatPos.identifier === seatPosition.identifier) {
        return {
          ...seatPos,
          selected: value,
        };
      }
      return seatPos;
    });
    dispatch(setSeatPositionsData({ ...seatPositionsData, seatPositions }));
  };

  const onAdditionalPositionUpdate = (position, value) => {
    const additionalPositions = seatPositionsData.additionalPositions.map((pos) => {
      if (pos.identifier === position.identifier) {
        return {
          ...pos,
          selectedOption: value,
        };
      }
      return pos;
    });
    dispatch(setSeatPositionsData({ ...seatPositionsData, additionalPositions }));
  };

  const saveSelectedOption = (value, seatPosition) => {
    const seatPositions = seatPositionsData.seatPositions.map((seatPos) => {
      if (seatPos.identifier === seatPosition.identifier) {
        return {
          ...seatPos,
          selectedOption: value || null,
        };
      }
      return seatPos;
    });
    dispatch(setSeatPositionsData({ ...seatPositionsData, seatPositions }));
  };

  const onSelectFeatureOption = (seatPosition, feature, value) => {
    const seatPositions = seatPositionsData.seatPositions.map((seatPos) => {
      if (seatPos.identifier === seatPosition.identifier) {
        return {
          ...seatPos,
          featureSelection: seatPos.featureSelection.map((featureItem) => {
            return featureItem.identifier === feature.identifier
              ? {
                  ...featureItem,
                  selectedOption: value || null,
                }
              : featureItem;
          }),
        };
      }
      return seatPos;
    });
    dispatch(setSeatPositionsData({ ...seatPositionsData, seatPositions }));
  };

  const lastAdditionalPositionIndex = additionalPositions && additionalPositions.length - 1;

  return (
    <div className={clsx(classes.root, !additionalPositionsLoaded && 'isLoadInprogress')}>
      {!additionalPositionsLoaded && (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      )}
      <>
        {seatPositions &&
          seatPositions.length > 0 &&
          seatPositions.map((seatPosition, i) => (
            <div className={classes.section} key={i}>
              <FormControlLabel
                labelPlacement="end"
                control={
                  <Switch
                    checked={seatPosition.selected}
                    classes={{
                      root: classes.switchRoot,
                      switchBase: classes.colorSwitchBase,
                      checked: classes.colorChecked,
                      track: classes.colorBar,
                    }}
                    onClick={(e) => onToggle(seatPosition, e.target.checked)}
                    name={seatPosition.identifier}
                  />
                }
                label={seatPosition.title}
              />
              <RadioGroup
                value={
                  get(seatPosition, 'selectedOption') || get(seatPosition.options[0], 'identifier', null)
                }
                className={classes.optionsGroup}
                onChange={(e) => saveSelectedOption(e.target.value, seatPosition)}
              >
                {seatPosition.options.map((option, i) => (
                  <FormControlLabel
                    key={i}
                    value={option.identifier}
                    control={
                      <Radio disableRipple classes={{ root: classes.radio, checked: classes.checked }} />
                    }
                    label={option.title}
                  />
                ))}
              </RadioGroup>
              <div className={classes.featureOptions}>
                {seatPosition.featureSelection.map((feature, i) => (
                  <div className={classes.question} key={i}>
                    <Typography className={classes.questionTitle} component="span" variant="body2">
                      <span className={classes.questionTitleInner}>
                        <span>{feature.title + (feature.required ? '*' : '')}</span>
                        {translationExists(`amountPrice.featureSelectionTitle.${feature.identifier}`) && (
                          <InfoTooltip
                            className={classes.tooltip}
                            tooltipText={t(`amountPrice.featureSelectionTitle.${feature.identifier}`)}
                          />
                        )}
                      </span>
                    </Typography>
                    <FormControl variant="filled" className={classes.selectorOutline} error={!!feature.error}>
                      <Select
                        classes={{
                          root: classes.answerSelector,
                          notchedOutline: classes.selectorNotchedOutline,
                        }}
                        autoFocus
                        variant="outlined"
                        value={get(feature, 'selectedOption', 0) || 0}
                        onChange={(e) => onSelectFeatureOption(seatPosition, feature, e.target.value)}
                      >
                        <MenuItem className={classes.MenuItemOption} key={'default'} value={0}>
                          <Typography className={classes.toolTitle} noWrap variant="body2">
                            {t('pleaseSelect')}
                          </Typography>
                        </MenuItem>
                        {feature.options &&
                          feature.options.map((answer, i) => (
                            <MenuItem className={classes.MenuItemOption} key={i} value={answer.identifier}>
                              <Typography className={classes.toolTitle} noWrap variant="body2">
                                {answer.title}
                              </Typography>
                            </MenuItem>
                          ))}
                      </Select>
                      {feature.error && <FormHelperText>{getFeatureErrorText(feature)}</FormHelperText>}
                    </FormControl>
                  </div>
                ))}
              </div>
            </div>
          ))}
        {additionalPositions &&
          additionalPositions.length > 0 &&
          additionalPositions.map((pos, i) => (
            <div
              key={pos.identifier}
              className={clsx(
                classes.featureOptions,
                i === lastAdditionalPositionIndex && classes.lastFeatureOption
              )}
            >
              <Typography className={classes.additionalSectionTitle}>
                {t(`amountPrice.additionalSectionTitle.${pos.identifier}`)}
              </Typography>
              <div className={classes.selectSection}>
                {pos.hint ? (
                  <Typography variant="body2" className={classes.posHint}>
                    {pos.hint}
                  </Typography>
                ) : (
                  <div className={classes.posQuestion} key={i}>
                    <Select
                      className={classes.posSelectorOutline}
                      classes={{
                        root: classes.answerSelector,
                        notchedOutline: classes.selectorNotchedOutline,
                      }}
                      autoFocus
                      variant="outlined"
                      value={get(pos, 'selectedOption', 0) || 0}
                      onChange={(e) => onAdditionalPositionUpdate(pos, e.target.value)}
                    >
                      <MenuItem className={classes.MenuItemOption} key={'default'} value={0}>
                        <Typography className={classes.toolTitle} noWrap variant="body2">
                          {t('pleaseSelect')}
                        </Typography>
                      </MenuItem>
                      {pos.options &&
                        pos.options.map((answer, i) => (
                          <MenuItem className={classes.MenuItemOption} key={i} value={answer}>
                            <Typography className={classes.toolTitle} noWrap variant="body2">
                              {answer}
                            </Typography>
                          </MenuItem>
                        ))}
                    </Select>
                    <Typography component="span" variant="body2">
                      {pos.title}
                    </Typography>
                  </div>
                )}
              </div>
            </div>
          ))}
      </>
    </div>
  );
};

ProductVariant.propTypes = {
  seatPositionsData: PropTypes.object,
  additionalPositionsLoaded: PropTypes.bool,
};

export default ProductVariant;
